import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  ResourceCardScreen,
  makeSelectResourceCardVm,
  platformProbeResource,
  platformResource,
  resourcesReloadRequested,
  type PlatformResourceArgs,
  type ResourceCardActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { CONNECTION_FORMS, RESOURCE_DIAGNOSTICS, RESOURCE_ENTRIES } from '../../composition/connectionForms'
import { useConnectors } from './useConnectors'
import { useMyAccess } from './useMyAccess'

/**
 * Binding ресурсного окна: rid берётся из контекста окна (адрес или `?w=`),
 * а не из глобальной настройки «активный ресурс» (D-064). Args стабильны и
 * совпадают у подписки и `select` — иначе selector читал бы чужой ключ кэша.
 * Панель диагностики и входы в окна — от native-пакета вида из реестра composition.
 */
export function ResourceCardBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const args = useMemo<PlatformResourceArgs>(() => ({ resourceId }), [resourceId])
  platformResource.useQuerySubscription(resourceId === '' ? skipToken : args)
  const access = useMyAccess()
  const { registry: connectors } = useConnectors()
  const [probe, probeState] = platformProbeResource.useMutation()

  const selectQuery = useMemo(() => platformResource.select(args), [args])
  const query = useAppSelector((state: RootState) => selectQuery(state))
  const selectVm = useMemo(() => makeSelectResourceCardVm(), [])
  const vm = selectVm({ query, access, forms: CONNECTION_FORMS, connectors, probe: probeState, entries: RESOURCE_ENTRIES })

  const actions = useMemo<ResourceCardActions>(
    () => ({
      reload: () => dispatch(resourcesReloadRequested({ resourceId })),
      close: window.close,
      edit: () => openWindow(PLATFORM_WINDOWS.resourceEdit, { rid: resourceId }),
      probe: () => void probe(args),
      // Окно пакета вида — с rid ЭТОГО ресурса из контекста окна, а не «текущего ресурса».
      openEntry: (kind) => openWindow(kind, { rid: resourceId }),
    }),
    [args, dispatch, openWindow, probe, resourceId, window.close],
  )

  const Diagnostics = vm.resource === null ? undefined : RESOURCE_DIAGNOSTICS[vm.resource.kind]
  return (
    <ResourceCardScreen
      vm={vm}
      actions={actions}
      {...(Diagnostics !== undefined && vm.diagnostics.allowed
        ? { diagnostics: <Diagnostics key={resourceId} resourceId={resourceId} /> }
        : {})}
    />
  )
}
